(function initSlateTierCartGuard(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  root.SlateTierCartGuard = api;
  api.install(root);
})(typeof window !== 'undefined' ? window : globalThis, function createSlateTierCartGuard() {
  'use strict';

  const CORE_VARIANT_ID = '50433372979243';
  const PREMIUM_VARIANT_ID = '50433380515883';
  const CORE_SKU = 'SS-CORE-ANNUAL';
  const PREMIUM_SKU = 'SS-PREMIUM-ANNUAL';
  const CORE_PRODUCT_PATH = '/products/slate-serif-core-12-month-digital-planner';
  const PREMIUM_PRODUCT_PATH = '/products/slate-serif-premium-12-month-digital-planner';

  const PREMIUM_REPLACED_CORE_MESSAGE =
    'Premium replaced Core because Premium already includes the complete Core planner. You will only be charged for Premium.';
  const PREMIUM_ALREADY_SELECTED_MESSAGE =
    'Premium is already in your cart and includes Core, so Core was not added.';
  const CART_CORRECTED_MESSAGE =
    'Your cart was corrected to one planner tier. Premium takes priority over Core.';
  const CHECKOUT_UNAVAILABLE_MESSAGE =
    'We could not verify your planner selection. Please refresh the page before checkout.';

  function asId(value) {
    if (value === null || value === undefined) return '';
    return String(value);
  }

  function tierForVariantId(value) {
    const id = asId(value);
    if (id === CORE_VARIANT_ID) return 'core';
    if (id === PREMIUM_VARIANT_ID) return 'premium';
    return null;
  }

  function tierForCartItem(item) {
    const variantTier = tierForVariantId(item?.variant_id ?? item?.id);
    if (variantTier) return variantTier;
    if (item?.sku === CORE_SKU) return 'core';
    if (item?.sku === PREMIUM_SKU) return 'premium';
    return null;
  }

  function hasPlannerTier(cart) {
    return Boolean(cart?.items?.some((item) => tierForCartItem(item)));
  }

  function isPlannerProductPath(pathname) {
    const normalized = String(pathname || '').replace(/\/+$/, '');
    return normalized === CORE_PRODUCT_PATH || normalized === PREMIUM_PRODUCT_PATH;
  }

  /**
   * Returns the smallest Shopify Ajax Cart updates needed to enforce:
   * one annual planner, Premium wins over Core, and quantity is always one.
   */
  function planCartNormalization(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const core = items.filter((item) => tierForCartItem(item) === 'core');
    const premium = items.filter((item) => tierForCartItem(item) === 'premium');
    const keep = premium.length > 0 ? premium[0] : core[0];
    const tierItems = premium.length > 0 ? premium.concat(core) : core;
    const updates = {};

    for (const item of tierItems) {
      if (!item?.key) continue;
      const desiredQuantity = item === keep ? 1 : 0;
      if (Number(item.quantity) !== desiredQuantity) {
        updates[item.key] = desiredQuantity;
      }
    }

    const changed = Object.keys(updates).length > 0;
    const replacedCore = premium.length > 0 && core.length > 0;

    return {
      changed,
      updates,
      keptTier: premium.length > 0 ? 'premium' : core.length > 0 ? 'core' : null,
      replacedCore,
    };
  }

  function cloneFormData(body) {
    const clone = new FormData();
    body.forEach((value, key) => clone.append(key, value));
    return clone;
  }

  function parseJsonBody(body) {
    if (typeof body !== 'string') return null;
    try {
      return JSON.parse(body);
    } catch (_error) {
      return null;
    }
  }

  function parseAddBody(body) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const id = asId(body.get('id'));
      if (!id) return null;
      return {
        format: 'form',
        original: body,
        items: [{ id, quantity: Number(body.get('quantity') || 1) }],
      };
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      const id = asId(body.get('id'));
      if (!id) return null;
      return {
        format: 'params',
        original: body,
        items: [{ id, quantity: Number(body.get('quantity') || 1) }],
      };
    }

    const json = parseJsonBody(body);
    if (!json || typeof json !== 'object') return null;

    if (Array.isArray(json.items)) {
      return {
        format: 'json-batch',
        original: json,
        items: json.items.map((item) => ({ ...item, id: asId(item.id) })),
      };
    }

    if (json.id !== undefined) {
      return {
        format: 'json-single',
        original: json,
        items: [{ ...json, id: asId(json.id) }],
      };
    }

    return null;
  }

  function serializeAddBody(parsed, items) {
    if (parsed.format === 'form') {
      const body = cloneFormData(parsed.original);
      body.set('id', items[0].id);
      body.set('quantity', String(items[0].quantity ?? 1));
      return body;
    }

    if (parsed.format === 'params') {
      const body = new URLSearchParams(parsed.original);
      body.set('id', items[0].id);
      body.set('quantity', String(items[0].quantity ?? 1));
      return body;
    }

    if (parsed.format === 'json-batch') {
      return JSON.stringify({ ...parsed.original, items });
    }

    return JSON.stringify({ ...parsed.original, ...items[0] });
  }

  /**
   * Prepares a cart/add request and the pre-add cleanup it requires.
   */
  function prepareAdd(body, cart) {
    const parsed = parseAddBody(body);
    if (!parsed) {
      return { handled: false, body, preUpdates: {}, blocked: false, notice: null };
    }

    const incomingCore = parsed.items.filter((item) => tierForVariantId(item.id) === 'core');
    const incomingPremium = parsed.items.filter((item) => tierForVariantId(item.id) === 'premium');
    if (incomingCore.length === 0 && incomingPremium.length === 0) {
      return { handled: false, body, preUpdates: {}, blocked: false, notice: null };
    }

    const cartItems = Array.isArray(cart?.items) ? cart.items : [];
    const currentCore = cartItems.filter((item) => tierForCartItem(item) === 'core');
    const currentPremium = cartItems.filter((item) => tierForCartItem(item) === 'premium');
    const preUpdates = {};
    const nonTierItems = parsed.items.filter((item) => !tierForVariantId(item.id));
    let nextItems;
    let notice = null;

    if (incomingPremium.length > 0) {
      for (const item of currentCore.concat(currentPremium)) {
        if (item?.key) preUpdates[item.key] = 0;
      }
      nextItems = nonTierItems.concat([{ ...incomingPremium[0], id: PREMIUM_VARIANT_ID, quantity: 1 }]);
      if (currentCore.length > 0 || incomingCore.length > 0) {
        notice = PREMIUM_REPLACED_CORE_MESSAGE;
      }
    } else if (currentPremium.length > 0) {
      for (const item of currentCore) {
        if (item?.key) preUpdates[item.key] = 0;
      }
      nextItems = nonTierItems;
      notice = PREMIUM_ALREADY_SELECTED_MESSAGE;
    } else {
      for (const item of currentCore) {
        if (item?.key) preUpdates[item.key] = 0;
      }
      nextItems = nonTierItems.concat([{ ...incomingCore[0], id: CORE_VARIANT_ID, quantity: 1 }]);
    }

    const blocked = nextItems.length === 0;
    return {
      handled: true,
      body: blocked ? body : serializeAddBody(parsed, nextItems),
      preUpdates,
      blocked,
      notice,
    };
  }

  function requestPath(input, win) {
    const raw = typeof input === 'string' ? input : input?.url;
    if (!raw) return '';
    try {
      return new URL(raw, win.location.origin).pathname.replace(/\/+$/, '');
    } catch (_error) {
      return '';
    }
  }

  function requestSections(body) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const values = body.getAll('sections').map(String).filter(Boolean);
      return values.length > 0 ? values[values.length - 1] : null;
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return body.get('sections');
    }
    const json = parseJsonBody(body);
    return json?.sections ?? null;
  }

  function jsonResponse(payload, status) {
    return new Response(JSON.stringify(payload), {
      status,
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
    });
  }

  function install(win) {
    if (!win || win.__slateTierCartGuardInstalled) return;
    win.__slateTierCartGuardInstalled = true;

    const nativeFetch = win.fetch.bind(win);
    const doc = win.document;
    let mutationQueue = Promise.resolve();
    let checkoutInProgress = false;

    function setCheckoutControlsDisabled(disabled) {
      for (const control of doc.querySelectorAll(
        'button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]'
      )) {
        if ('disabled' in control) control.disabled = disabled;
      }
    }

    function queueMutation(task) {
      const next = mutationQueue.then(task, task);
      mutationQueue = next.catch(() => undefined);
      return next;
    }

    function showNotice(message, isError) {
      if (!message || !doc?.body) return;
      let notice = doc.getElementById('slate-tier-cart-guard-notice');
      if (!notice) {
        notice = doc.createElement('div');
        notice.id = 'slate-tier-cart-guard-notice';
        notice.setAttribute('role', 'status');
        notice.setAttribute('aria-live', 'polite');
        Object.assign(notice.style, {
          position: 'fixed',
          zIndex: '2147483647',
          insetInline: 'max(16px, calc((100vw - 720px) / 2))',
          top: '16px',
          padding: '14px 18px',
          borderRadius: '8px',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.18)',
          font: '600 14px/1.4 system-ui, sans-serif',
          textAlign: 'center',
        });
        doc.body.appendChild(notice);
      }
      notice.style.background = isError ? '#7f1d1d' : '#173c32';
      notice.style.color = '#ffffff';
      notice.textContent = message;
      notice.hidden = false;
      win.clearTimeout(notice.__slateHideTimer);
      notice.__slateHideTimer = win.setTimeout(() => {
        notice.hidden = true;
      }, 7000);
    }

    function updateAcceleratedCheckoutVisibility(cart) {
      const hidden = hasPlannerTier(cart);
      for (const container of doc.querySelectorAll('.additional-checkout-buttons')) {
        container.hidden = hidden;
        container.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      }

      const productCheckoutHidden = hidden || isPlannerProductPath(win.location.pathname);
      for (const container of doc.querySelectorAll('.shopify-payment-button')) {
        container.hidden = productCheckoutHidden;
        container.setAttribute('aria-hidden', productCheckoutHidden ? 'true' : 'false');
      }
    }

    async function readCart() {
      const response = await nativeFetch('/cart.js', {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Cart read failed with ${response.status}`);
      return response.json();
    }

    async function applyUpdates(updates, sections) {
      if (Object.keys(updates).length === 0) return null;
      const payload = { updates };
      if (sections) {
        payload.sections = sections;
        payload.sections_url = win.location.pathname;
      }
      const response = await nativeFetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Cart correction failed with ${response.status}`);
      return response;
    }

    async function normalizeCart(cart, sections) {
      const current = cart?.items ? cart : await readCart();
      const plan = planCartNormalization(current);
      updateAcceleratedCheckoutVisibility(current);
      if (!plan.changed) return { cart: current, response: null, plan };

      const response = await applyUpdates(plan.updates, sections);
      const normalized = await response.clone().json();
      updateAcceleratedCheckoutVisibility(normalized);
      return { cart: normalized, response, plan };
    }

    async function guardedAdd(input, init) {
      const current = await readCart();
      const prepared = prepareAdd(init?.body, current);
      const sections = requestSections(init?.body);

      if (Object.keys(prepared.preUpdates).length > 0) {
        await applyUpdates(prepared.preUpdates, null);
      }

      if (prepared.notice) showNotice(prepared.notice, false);
      if (prepared.blocked) {
        return jsonResponse(
          {
            status: 422,
            message: PREMIUM_ALREADY_SELECTED_MESSAGE,
            description: PREMIUM_ALREADY_SELECTED_MESSAGE,
          },
          422
        );
      }

      const response = await nativeFetch(input, prepared.handled ? { ...init, body: prepared.body } : init);
      if (!response.ok) return response;

      const result = await normalizeCart(null, sections);
      return result.response ?? response;
    }

    async function guardedCartMutation(input, init) {
      const response = await nativeFetch(input, init);
      if (!response.ok) return response;

      let cart = null;
      try {
        cart = await response.clone().json();
      } catch (_error) {
        return response;
      }

      const result = await normalizeCart(cart, requestSections(init?.body));
      if (result.plan.replacedCore) showNotice(CART_CORRECTED_MESSAGE, false);
      return result.response ?? response;
    }

    win.fetch = function guardedFetch(input, init) {
      const path = requestPath(input, win);
      if (path === '/cart/add.js' || path === '/cart/add') {
        return queueMutation(() => guardedAdd(input, init || {}));
      }
      if (
        path === '/cart/change.js' ||
        path === '/cart/change' ||
        path === '/cart/update.js' ||
        path === '/cart/update'
      ) {
        return queueMutation(() => guardedCartMutation(input, init || {}));
      }
      return nativeFetch(input, init);
    };

    async function continueToCheckout(trigger) {
      if (checkoutInProgress) return;
      checkoutInProgress = true;
      if (trigger && 'disabled' in trigger) trigger.disabled = true;

      try {
        await queueMutation(async () => {
          const result = await normalizeCart(null, null);
          const verification = planCartNormalization(result.cart);
          if (verification.changed) throw new Error('Planner cart remained invalid after correction');
        });
        win.location.assign('/checkout');
      } catch (error) {
        console.error('[Slate tier guard] Checkout blocked:', error);
        showNotice(CHECKOUT_UNAVAILABLE_MESSAGE, true);
        checkoutInProgress = false;
        if (trigger && 'disabled' in trigger) trigger.disabled = false;
      }
    }

    doc.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const trigger = target?.closest(
          'button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]'
        );
        if (!trigger) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        continueToCheckout(trigger);
      },
      true
    );

    doc.addEventListener(
      'submit',
      (event) => {
        const submitter = event.submitter;
        const isCheckout =
          submitter?.getAttribute?.('name') === 'checkout' ||
          event.target?.getAttribute?.('action')?.includes('/checkout');
        if (!isCheckout) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        continueToCheckout(submitter);
      },
      true
    );

    const reconcileLoadedCart = (restoreCheckoutControls) => {
      queueMutation(async () => {
        try {
          const result = await normalizeCart(null, null);
          if (result.plan.changed) {
            showNotice(CART_CORRECTED_MESSAGE, false);
            win.location.reload();
            return;
          }
          if (restoreCheckoutControls) setCheckoutControlsDisabled(false);
        } catch (error) {
          console.error('[Slate tier guard] Initial cart verification failed:', error);
        }
      });
    };

    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', reconcileLoadedCart, { once: true });
    } else {
      reconcileLoadedCart();
    }
    win.addEventListener('pageshow', () => {
      checkoutInProgress = false;
      reconcileLoadedCart(true);
    });
  }

  return {
    CORE_VARIANT_ID,
    PREMIUM_VARIANT_ID,
    CORE_SKU,
    PREMIUM_SKU,
    CORE_PRODUCT_PATH,
    PREMIUM_PRODUCT_PATH,
    tierForVariantId,
    tierForCartItem,
    hasPlannerTier,
    isPlannerProductPath,
    planCartNormalization,
    prepareAdd,
    install,
  };
});
